/* eslint-disable react/no-danger */
import { h, render } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import FocusTrap from 'focus-trap-react';
import clsx from 'clsx';
import ArrowRight from '../utils/icons/arrow.svg';
import ArrowChevron from '../utils/icons/chevron.svg';
import CloseIcon from '../utils/icons/close.svg';

export default (element, data) => {
  const init = async () => {
    render(<PromoBar />, element);
  };

  const PromoBar = () => {
    const items = data?.promo_bar_items || [];
    const autoplaySpeed = (data?.autoplaySpeed ?? 0) * 1000;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [fade, setFade] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalItem, setModalItem] = useState(null);
    const [reduceMotion, setReduceMotion] = useState(false);
    const [modalAnimationState, setModalAnimationState] = useState('closed'); // 'closed', 'opening', 'open', 'closing'

    const modalRef = useRef(null);
    const prefersReducedMotion = useRef(false);

    useEffect(() => {
      prefersReducedMotion.current = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      setReduceMotion(prefersReducedMotion.current);
    }, []);

    useEffect(() => {
      if (items.length <= 1 || reduceMotion || autoplaySpeed <= 0)
        return undefined;

      const interval = setInterval(() => {
        setFade(false);
        setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % items.length);
          setFade(true);
        }, 300);
      }, autoplaySpeed);

      return () => clearInterval(interval);
    }, [items.length, autoplaySpeed, reduceMotion]);

    const closeModal = () => {
      setModalAnimationState('closing');
      setTimeout(() => {
        setShowModal(false);
        setModalAnimationState('closed');
      }, 500); // Match animation duration
    };

    useEffect(() => {
      const mainContent = document.querySelector('main');
      if (mainContent) {
        if (showModal) {
          mainContent.setAttribute('inert', '');
        } else {
          mainContent.removeAttribute('inert');
        }
      }
      document.body.style.overflow = showModal ? 'hidden' : '';

      const handleKeyDown = (event) => {
        if (event.key === 'Escape' && showModal) {
          closeModal();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [showModal]);

    const openModal = (item) => {
      setModalItem(item);
      setShowModal(true);
      setModalAnimationState('opening');
      // Delay the 'open' state to allow the modal to mount first
      setTimeout(() => {
        setModalAnimationState('open');
      }, 50);
    };

    if (!items.length) return null;

    const predefinedColors = {
      'white-on-black': 'text-white bg-bg-3',
      'black-on-white': 'text-bg-3 bg-white',
    };

    const modalClasses = clsx(
      'fixed inset-0 z-[9999999] overflow-y-auto transition-transform duration-500 ease-in-out',
      modalAnimationState === 'open' ? 'translate-y-0' : '-translate-y-full',
      modalAnimationState === 'closed' && 'pointer-events-none',
      predefinedColors[data?.promotionalBarColors || 'white-on-black'],
    );

    const contentClasses = clsx(
      'flex items-center justify-center transform transition-all duration-500 ease-in-out min-h-screen',
      modalAnimationState === 'open'
        ? 'opacity-100 translate-y-0 delay-300'
        : 'opacity-0 translate-y-4',
    );

    return (
      <div
        className={clsx(
          predefinedColors[data?.promotionalBarColors || 'white-on-black'],
          'relative h-[34px] tablet:h-[38px] overflow-hidden',
        )}
      >
        <div
          className={clsx(
            'transition-opacity duration-500 ease-in-out absolute inset-0',
            fade ? 'opacity-100' : 'opacity-0',
          )}
        >
          <PromoBarItem
            showModal={showModal}
            key={currentIndex}
            {...items[currentIndex]}
            onOpenModal={() => openModal(items[currentIndex])}
          />
        </div>

        {/* Modal */}
        {showModal && (
          <div
            className={modalClasses}
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-modal-title"
            aria-describedby="promo-modal-desc"
          >
            <FocusTrap
              focusTrapOptions={{
                initialFocus: false,
                allowOutsideClick: true,
                clickOutsideDeactivates: true,
                escapeDeactivates: false,
              }}
            >
              <div className="min-h-screen" ref={modalRef}>
                <div className={contentClasses}>
                  <button
                    className="absolute top-[19px] right-[19px] laptop:top-[49px] laptop:right-[49px]"
                    onClick={closeModal}
                    aria-label="Close promo modal"
                  >
                    <CloseIcon
                      className={clsx(
                        data?.promotionalBarColors === 'white-on-black'
                          ? 'text-white'
                          : 'text-black',
                      )}
                    />
                  </button>
                  <div className="flex flex-col laptop:flex-row items-start justify-between px-5 py-12 laptop:p-[80px] gap-x-8 gap-y-[4rem] w-full">
                    {[1, 2, 3].map((i) => {
                      const title = modalItem?.[`title_${i}`];
                      const description = modalItem?.[`description_${i}`];
                      const label = modalItem?.[`cta_label_${i}`];
                      const ctaLink = modalItem?.[`cta_link_${i}`];

                      if (!title && !description && !ctaLink) return null;

                      return (
                        <div
                          key={i}
                          className="w-full laptop:max-w-[334px] justify-self-center"
                        >
                          {title && (
                            <h3
                              id={`promo-modal-title-${i}`}
                              className="font-gotham uppercase text-2xl tablet:text-[30px] font-ultra leading-[1.75rem] tablet:leading-[2.125rem] mb-2"
                            >
                              {title}
                            </h3>
                          )}
                          {description && (
                            <p
                              id={`promo-modal-desc-${i}`}
                              className="text-sm mb-2 font-book leading-[1.25rem] whitespace-pre-line"
                            >
                              {description}
                            </p>
                          )}
                          {label && ctaLink && (
                            <div className="pt-[24px]">
                              <hr
                                className={clsx(
                                  'h-pxborder-0 mb-[27px]',
                                  data?.promotionalBarColors ===
                                    'white-on-black'
                                    ? 'bg-white'
                                    : 'bg-black',
                                )}
                              />
                              <a
                                href={ctaLink}
                                className="flex items-center justify-between font-gotham uppercase text-xs leading-4 tracking-[0.6px]"
                              >
                                <span>{label}</span>
                                <ArrowRight
                                  className={clsx(
                                    'rotate-180',
                                    data?.promotionalBarColors ===
                                      'white-on-black'
                                      ? 'stroke-white'
                                      : 'stroke-black',
                                  )}
                                />
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </FocusTrap>
          </div>
        )}
      </div>
    );
  };

  const hasModalContent = (item) => {
    return [1, 2, 3].some((i) =>
      Boolean(
        item[`title_${i}`] || item[`description_${i}`] || item[`cta_link_${i}`],
      ),
    );
  };

  const PromoBarItem = ({ message, link, onOpenModal, showModal, ...rest }) => {
    const hasContent = hasModalContent(rest);

    return (
      <div className="promotional-message flex justify-center items-center gap-x-1">
        <span dangerouslySetInnerHTML={{ __html: message }} />
        {hasContent && (
          <button
            className="underline flex flex-row items-center justify-center gap-x-1"
            onClick={onOpenModal}
            aria-expanded={showModal}
            aria-controls="promo-modal"
            tabIndex="0"
          >
            {link !== '' && <span>{link}</span>}
            <ArrowChevron
              className={clsx(
                ' transition-transform duration-250',
                showModal && 'rotate-180',
                data?.promotionalBarColors === 'white-on-black'
                  ? 'stroke-white'
                  : 'stroke-black',
              )}
            />
          </button>
        )}
      </div>
    );
  };

  init();
};
